/** @file Refits a mesh's triangles, edges, hull, deform keys and mirror maps to a new vertex count. */
import type { Draft } from 'immer';
import type { Doc, Id, MeshAtt } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';
import { clipDeformKeys } from './clipDeformKeys';
import { dropMirrorMaps } from './dropMirrorMaps';

/** The whole groups of `size` indices of `flat` whose indices are all < count, in order. */
function groupsBelow(flat: readonly number[], size: number, count: number): number[] {
  const kept: number[] = [];
  for (let i = 0; i + size <= flat.length; i += size) {
    const group = flat.slice(i, i + size);
    if (group.every((v) => v < count)) for (const v of group) kept.push(v);
  }
  return kept;
}

/** One INFO at `path` when `lost` groups of `what` were dropped. */
function reportLoss(ctx: CommandContext, lost: number, what: string, path: string): void {
  if (lost > 0) ctx.warnings.push({ code: 'INFO', message: `dropped ${lost} ${what} that used removed vertices`, params: { count: lost }, path });
}

/** Makes mesh `id` consistent with `count` vertices (§5.2, §6.14); each kind of loss adds one INFO warning. */
export function fitMeshToCount(draft: Draft<Doc>, id: Id, mesh: MeshAtt, count: number, ctx: CommandContext): void {
  const triangles = groupsBelow(mesh.triangles, 3, count);
  reportLoss(ctx, (mesh.triangles.length - triangles.length) / 3, 'triangles', 'triangles');
  if (triangles.length === 0 && mesh.triangles.length > 0) ctx.warnings.push({ code: 'INFO', message: 'no triangles are left: the mesh renders nothing until mesh.setTriangles', path: 'triangles' });
  mesh.triangles = triangles;
  if (mesh.edges !== undefined) {
    const edges = groupsBelow(mesh.edges, 2, count);
    reportLoss(ctx, (mesh.edges.length - edges.length) / 2, 'edges', 'edges');
    mesh.edges = edges;
  }
  if (mesh.hull > count) {
    ctx.warnings.push({ code: 'INFO', message: `hull reduced from ${mesh.hull} to ${count} vertices`, params: { from: mesh.hull, to: count }, path: 'hull' });
    mesh.hull = count;
  }
  clipDeformKeys(draft, id, count, ctx);
  if (dropMirrorMaps(draft, mesh)) {
    ctx.warnings.push({ code: 'INFO', message: 'removed the mirror vertex maps of this mesh and its counterpart (they indexed the old vertices)', path: 'mirrorVertexMap' });
  }
}
